export interface WhatsNewEntry {
  /** ISO date YYYY-MM-DD. */
  date: string;
  items: string[];
}

/** Newest first. New entries go at the top. */
export const WHATS_NEW_ENTRIES: WhatsNewEntry[] = [
  {
    date: '2026-05-27',
    items: [
      'Added Scintillant Amulet +1 and Scintillant Sash +1 to the Masterwork of Menzoberranzan set, and filled in the Warlock Impending Doom weapon pair with the lower-rank Soul Harvest versions.',
      'Added Cracked Stormbind Tunic as a new Shirt item (IL 4600) with its Bloodletting equip bonus.',
      'Added Bulwark of the Eternal Zulkirate as a new Armor item (IL 4300) with the Ruthless Might (Greater) equip bonus.',
      'Filled in the Cleric Impending Doom weapon pair (Warden of the Last Rite, Sigil of the Abyss) with their lower-rank Soul Harvest versions, from Epic up to Mythic; the set bonus now scales by rank instead of always using the top-tier values.',
      'Added the four Arbiter class features for Cleric, so Arbiter builds can now pick their class features in the character editor.',
      'Alias / Bind File Builder gained party & queue and interact shortcuts and a Toggles section (combat log, netgraph, FPS, high-detail, hard-target-lock, walk, auto-run — each switchable between a compact bind or a Notify version that bubbles the ON/OFF state over your head), plus "F-key VIP" and "Ctrl QoL" layout presets — and the Quick-start presets now toggle (click to apply, click again to remove, keeping anything another active preset still uses); the Aliases & Binds guide now covers one-press toggles, self-rewriting toggle aliases, and spaced-channel commands. Fixed the /pet shortcut to summon/dismiss your companion.',
      'Added a "Game default binds" section to the File Builder — every action from the in-game Keybinds settings (movement, camera, powers, item tray, mount, artifact, Bard performance mode, and UI windows) paired with the command behind it, so you can remap any of them to a new key from one place; off by default.',
    ],
  },
  {
    date: '2026-05-26',
    items: [
      'New Alias/Bind File Builder under Other Info: a single page with a sticky output panel showing live previews of both CommandAliases.txt and your bind file as you build them. Pick from a curated library of chat aliases and key shortcuts, apply one-click presets (Bard kit, QoL pack, Combat essentials), capture keys by pressing them, and assign multiple keys (or parameter values, for things like /iexec) to any alias. Import existing files to edit and re-export.',
    ],
  },
  {
    date: '2026-05-25',
    items: [
      'New Upgrade Cost Calculator: plan AD spend through the quality tiers for enchantments, artifacts, insignias, and companions — with auto-picked mote, ward, and payment-method choices per step, buy-vs-upgrade comparison, and a best RP-per-AD finder.',
      'Companion Enhancement Powers (Perfect Vision, Potent Precision, Precision, Potency, etc.) now surface as conditional bonuses in the character editor — opt in to bake their +9% on-hit ceiling into your static stats.',
      'Sidebar consolidated: Gear, Companions, and Mounts & Insignias are now grouped under a single "Gear & Equipment" section with expandable subsections.',
    ],
  },
  {
    date: '2026-05-24',
    items: [
      'Added Cindersilk Robes (Epic IL 4600 Warlock/Wizard chest, Ruthless Might equip bonus) and the Mystic Conduit Ink shirt and trousers (Legendary IL 3600, Critical Tactics and Butcher\'s Precision equip bonuses) from Soul Harvest (Advanced).',
      'Optimizer now adapts to your device — fewer parallel workers and a smaller fitness cache on low-memory machines, so long runs no longer crash the tab.',
      'Optimizer and stat totals now treat "Part of the Pack" companion buffs (Raptor\'s / Feral Raptor\'s Instincts) as conditional — the party-stack Power/Awareness only applies when you also have the matching power slotted, so the optimizer will pick Raptor\'s Instincts when allies are running it.',
    ],
  },
  {
    date: '2026-05-23',
    items: [
      'Bard data pass: at-wills, shared and Minstrel paragon encounters refreshed against in-game tooltips; full Minstrel and Songblade feat trees added (20 feats across 5 OR-slots per paragon); shared, Minstrel, and Songblade class features added (12 total); Bard class skills (Harmonize, Critical Tuning, Truly Inspired) added.',
      'Guild directory: the quest board card style is now the default — switch back to the heraldic banner from the style toggle if you prefer it.',
      'Added Shroomwood Amulet and Shroomwood Sash (Legendary IL 1700) to the Masterwork of Menzoberranzan set.',
      'Added the Encroaching Frost Lockbox to the lockbox list.',
      'Lockbox / pack listings now show a thumbnail for each mount, companion, and artifact reward, and the name links straight to the matching list page pre-filtered to that item.',
      'Racial traits across all 14 races verified against in-game tooltips and wired into the character editor — bonuses now contribute to stat totals where applicable. Several traits were renamed to match current game data: Drow (Trance → Honed Defenses), Menzoberranzan Renegade (Trance → Honed Defenses), Dwarf (Cast-Iron Stomach → Cast-Iron), Gith (Decentralized Mind/Githborn Discipline → Gith Advantage/Gith Endurance), and Aasimar (Celestial Legacy/Healing Hands → Celestial Presence/Celestial Superiority). Metallic Dragonborn\'s second trait corrected from Draconic Heritage to Dragonborn Fury. Human\'s "Heroic Effort" removed pending verification. Conditional and party-aura effects (slow resist, on-attack debuff procs, on-combat speed bursts, knock/push resist, party-wide Max HP aura) are documented in tooltips but stay outside the player\'s own stat totals.',
      'Added the Wintermarked Skirmisher/Marcher armor set for Rogue/Cleric/Bard/Ranger (Head, Feet, Armor, Arms) from Jotunskar (Master), including two new equip bonuses (Combatant\'s Advantage 10-stack variant and Relentless Reserves).',
      'Added the Snowtusk mount (Mythic) with the Winter\'s Wrath combat power and Mystic Aura equip bonus.',
      'Added the Twitchspine the Clinging companion with the Twitchspine\'s Resilience player bonus (Defense proc on damage taken).',
      'Added the Sir Waddlelot companion with the Relentless Waddle player bonus (Critical Severity proc while running).',
      'Added the Wormungandr companion with the Continental Craving player bonus (Critical Strike + Power).',
      'Added the Enchanted Depthcured Hat (Mythic IL 3200) for Rogue/Cleric/Bard/Ranger from Lair of the Mad Dragon (Master).',
    ],
  },
  {
    date: '2026-05-21',
    items: [
      'Compare Builds removed from the side menu, and the Rotation Simulator moved to Labs.',
      'Guild directory: line breaks in descriptions now render — paragraphs are no longer collapsed into one block, and the existing 45 guild descriptions got a one-time pass to add paragraph breaks where they read as walls of text.',
    ],
  },
  {
    date: '2026-05-20',
    items: [
      'Landing page refreshed — hero banner, accent-colored section bands, the Community block promoted to the top with Guild List first, Guild Tools as its own section, and quick-jump chips to sub-pages (mount list, EHP calculator, companion stable sim, etc.). A "Recently added" teaser at the bottom mirrors What\'s New.',
      'Refining (RP) calculator, Character build compare, and Rainer\'s Contests are now linked from the side menu — previously they were reachable only by direct URL.',
      'New Jotunskar (Master) ring set — seven rings each at Mythic IL 5700 (Coldsilver) and Celestial IL 6000 (Frostsilver), with their tier-specific equip bonuses (Circular Healing, Maiden\'s Advantage, Dashing Ranger, Charging Bull, Manticore\'s Mane Bite, Deity\'s Gift, Accumulating Precision).',
      'New Mythic IL 3000 True Ice companion gear from Jotunskar (Master) and Legendary IL 2850 Frostforged companion gear from The Biting Cold: The Encroaching Frost — all nine slots each (Belt, Girdle, Grimoire, Icon, Necklace, Ring, Sword Knot, Talisman, Tome).',
      'Guild directory: a guild can now list multiple languages instead of one — the submission form has a tag input (press Enter or comma to add each), and the filter / autocomplete update accordingly.',
      'Guild directory: listings can now tag their "most active time" (NA Evenings, EU Evenings, OCE/AU Primetime, Weekends, Active 24/7, etc.) — pick from the chip list when submitting or editing your guild, and filter the directory by time of day to find a guild that plays when you do.',
      'Guild directory: added "Drider" as a platform option for the Russian Neverwinter shard.',
      'Bug fixed in login.',
      'Fewer surprise sign-outs.',
    ],
  },
  {
    date: '2026-05-19',
    items: [
      'New Mythic IL 5700 Wintermarked armor set from Jotunskar (Master) — twelve pieces across three class flavors, each with its own equip bonus.',
      'New Celestial IL 4850 Deep-Riven trousers from Jotunskar (Master) — pair with the Frost-Riven shirts for a 2-piece set bonus.',
      'Guild directory filters now collapse spelling variants (case / whitespace), the Language filter is a multi-select sorted by popularity, and submission inputs offer "Did you mean …?" suggestions for near-misses.',
      'Fixed sign-in not persisting across page reloads.',
      'Guild directory got a visual pass — pick between two card styles (Heraldic or Quest Board) from the filter bar.',
      'Guild directory: owner edits and banner changes now go through admin re-approval — your previously-approved version stays live during review.',
      'Guild directory: owners now have a real Delete button on their own card.',
    ],
  },
  {
    date: '2026-05-18',
    items: [
      'Side menu reorganized — "Neverwinter Community" section is now at the top of the menu and includes a direct "Guild List" link to the Guilds & Alliances directory. The "About & What\'s New" link moved to the bottom of the menu.',
      'Guild directory: each listing now shows a "last updated" timer, and listings drop off after 6 months without an update. Owners can hit a new Refresh button on their card to reset the timer. The "Claim this listing" flow has been retired — to take over a stale entry, submit a fresh listing once the old one expires.',
      'Guild directory polish — markdown in descriptions, owner-uploadable banner images, a refreshed card layout, and listings sorted most-recently-updated first.',
      'Submitting a new guild now lets you attach a banner image right in the form, with a live preview. The banner goes live on your card the moment an admin approves the listing — no separate upload step.',
      'New gear items across feet, armor and shirt slots at Mythic / Legendary / Epic, plus several new equip bonuses.',
      'Rogue main-hand weapon now lists its 6 power-damage modifiers.',
    ],
  },
  {
    date: '2026-05-16',
    items: [
      'New Guilds & Alliances directory — browse 47 community guilds (seeded from the official Guilds of Neverwinter article), filter by platform, alliance, or language, and submit your own guild for inclusion. Signed-in users can also claim an existing listing to keep it up to date.',
      'Links to NW Hub now show a preview card (title, description, banner) when posted in Discord, Slack, Telegram, LinkedIn and similar.',
    ],
  },
  {
    date: '2026-05-15',
    items: [
      'Sign in with Discord or Google from the new login button in the top-right toolbar — unlocks the Account page, character sync across browsers, and managed shareable build links.',
      'Signing in with Discord now uses your Discord display name instead of showing "Anonymous". Existing accounts still showing "Anonymous" are healed automatically on the next Discord login.',
      'New Account page (from the user menu in the top-right) lets you change your display name and see which sign-in methods are linked to your profile.',
      'Character build share dialog now has a "Never" lifetime option for links that don\'t expire.',
      'New "Shared builds" page (from the user menu) lists every build you\'ve shared while signed in — extend or change a link\'s lifetime, copy the URL, or delete a share to revoke it.',
      'Characters can now be synced to your account. Each character in the editor list has a cloud toggle — opt in per character to push it to the server and have it pull back on any browser where you sign in. Off by default; local-only characters stay local.',
      'Fixed the Damage Math page — equations now render correctly again.',
    ],
  },
  {
    date: '2026-05-14',
    items: [
      'Mount Stable in the character editor now shows the mount icon (with quality border) in each card header and in the mount picker dropdown, making it easier to recognise which mount is in each slot at a glance.',
      'Character editor sidebar now has a Sections jump-nav — click any of the 15 sections to expand and scroll to it. The open/closed state of each section is also remembered across page reloads, and filled sections show an accent dot so you can see at a glance what\'s configured.',
      'Character editor now has a Share button — click it to get a short shareable link that anyone can open to load your full build (class, gear, enchants, boons, sim configs, everything) into their own editor. Pick a lifetime (1 day / 7 days / 30 days / 1 year / never) so abandoned links clean themselves up. Recipients see a preview banner with "Save as my character" before anything is saved locally.',
    ],
  },
  {
    date: '2026-05-12',
    items: [
      'Mount list now shows icons for 302 of 305 mounts, plus icons on combat powers (79/89) and equip powers (39/51) in both the mount table and the profile editor power picker.',
      'Mount list now shows the full equip power and combat power text inline (no need to hover for stats), adds a Stat filter for equip powers, and fixes mount icon vertical alignment for rows with multi-line content.',
      'Wider content cards (1100 → 1400px) so data tables like the mount list have room for all columns. Mount, companion, gear, insignia, insignia-bonus, companion-power, and enhancement-power lists all switch to a stacked card view at phone widths (≤600px) — one card per item, all fields labelled — instead of a sideways-scrolling table. Mount cards on mobile also show the equip-power and combat-power icons in the left column alongside the mount icon.',
    ],
  },
  {
    date: '2026-05-11',
    items: [
      'Companion list now shows each player bonus\'s full stat breakdown inline, and adds a Stat filter so you can narrow to companions that grant a specific rating (Power, Critical Strike, Deflect, etc.).',
      'Added confirmed icons for Rothe Traveler (mount), Shadar-kai Witch, and Armored Orc Wolf (companions).',
    ],
  },
  {
    date: '2026-05-10',
    items: [
      'Added new gear — Cleric weapons, rings, and Shirt / Trousers — plus the new Enchanted Healing set and several new equip bonuses.',
      'Added Captain Elaina Sartell companion with Enduring Craft enhancement (+6% Forte/Power).',
    ],
  },
  {
    date: '2026-05-09',
    items: [
      'Added the Zen Store new-character-promo Companion Choice Pack (Flapjack / Spined Devil / Minsc) to the Packs page.',
    ],
  },
  {
    date: '2026-05-08',
    items: [
      'Added three new combat enchantments (Fluid Aurora, Shifting Shards, Shattered Resolve) at Mythic and Celestial.',
      'Added two new standard enchantments — Sugilite and Obsidian — across all six rarities, with offense / defense / utility stat splits.',
      'Paladin rotation sim now consumes Divinity on its divinity-cost powers — they stall when the pool runs dry instead of casting infinitely.',
      'Paladin Justicar feats Burning Vengeance and Heroism are now applied during simulation.',
      'Warlock Hellbringer Soul Sparks now stack in the rotation sim, contributing steady-state damage scaling. Soul Scorch consumption is not yet modeled.',
      'Class mechanics (Soul Spark, Arcane Mastery, Smolder, Battlerage, Retaliate, …) are now applied during simulation — previously their effects were defined in data but never reached the engine. Wizard, Barbarian Blademaster, and Fighter Vanguard sim numbers will shift accordingly.',
    ],
  },
  {
    date: '2026-05-07',
    items: [
      'Squash Soup updated to its current in-game values (Critical Strike +5% / Critical Severity +5%). The previous version is kept as "Squash Soup (Old)" for reference.',
      'Added Oakenthorn Helm — Legendary head item (IL 4050, Paladin / Barbarian / Fighter) with the new Warden\'s Stamina equip power (stacking Stamina Regeneration and Awareness in combat).',
    ],
  },
  {
    date: '2026-05-06',
    items: [
      'Armor Kits and Jewels can now be slotted into character profiles — Armor Kits go in head / chest / arms / feet, Jewels in neck / waist / rings. Slots that have a kit show an amber indicator and stat summary, and the kit\'s rating + IL flow into all stat sources just like enchantments.',
      'External Buffs now includes Paladin auras (Aura of Wrath, Aura of Protection, Aura of Restoration) — toggle these on to model having a Paladin in your group.',
    ],
  },
  {
    date: '2026-05-04',
    items: [
      'New "Packs, Lockboxes & More" page — browse 71 lockboxes from 2013 through 2026 with their featured companion / artifact / mount / race rewards, searchable by lockbox or reward name and filterable by reward type. Zen-shop packs and other reward containers will be added here over time.',
      'Added Reaper\'s Challenge choice packs (Legendary Mount and Legendary Companion, 30 Reaper\'s Marks each) and Zen Store choice packs (Companion and Artifact). Switch between lockboxes and choice packs with the type filter, or search across both at once to find which sources offer a given reward.',
      'Each reward item now shows its bind state with three distinct pills — green Account Unlock (every character can claim a copy), teal Account Bound (one copy, movable across alts via shared bank), orange Character Bound (locked to one character) — plus a rarity tag where applicable. A legend at the top of the page explains each. Lockboxes, Zen Store packs, and event-currency packs are also visually distinct (indigo / gold / green accent stripe and badge color).',
      'About page now combines About, What\'s New, and Credits into a single tabbed view.',
    ],
  },
  {
    date: '2026-05-02',
    items: [
      'Wizard at-will sim tuned against an in-game combat log — Ray of Frost, Chilling Cloud and Magic Missile timings now match observed behavior, shifting AP/sec and DPS for these at-wills.',
      'Renamed the "Chat Aliases" page to "Aliases & Binds" (under Other Info) and added a section on key binds — basic /bind syntax, modifier combos, command chaining with $$, and saving / loading bind layouts.',
      'Aliases card reorganised around three primary uses — channel shortcuts, chat command shortcuts, and bind helpers — with a new note on using aliases to set up cycling binds (one key that does something different on every press).',
      'Added a "Useful In-Game Commands" reference card to the Aliases & Binds page.',
      'Binds card now also covers activation prefixes (+, ++) and the non-obvious numpad key names.',
      'Added a Bard Song Aliases reference card with copy-pasteable CommandAliases.txt entries for all 12 songs, bindable under one short alias each.',
      'Added two rings — Rotsteel Hoop of Corruption (Legendary) and The Claw of Covetous Flame (Mythic).',
    ],
  },
  {
    date: '2026-05-01',
    items: [
      'Cleric class fully reworked to match the current in-game state — encounters, dailies, feats, mechanics, etc. rebuilt from scratch across both Arbiter and Devout paths.',
      'Added 7 gear items across head, feet, chest, arms, rings, neck and waist — including the new 3-piece Malignant Energy set with the Sealing Parchment artifact.',
      'Four new equip powers added.',
    ],
  },
  {
    date: '2026-04-30',
    items: [
      'Cleric at-wills cleaned up — Sacred Flame and Scattering Light are now the two shared at-wills (Sacred Flame updated to threefold fire damage, Scattering Light promoted from encounter). Lance of Faith is now Arbiter-specific. Devout paragon now has Soothe (heal) and Blessing of Light (next-heal buff) as its at-wills.',
    ],
  },
  {
    date: '2026-04-29',
    items: [
      'Boons editor: fixed Advanced and Master tier "Varying Cost" calculation.',
      'Added a top-of-page progress bar that shows while a route is loading, so navigations to heavier pages (character editor, gear, sim) no longer feel unresponsive.',
    ],
  },
  {
    date: '2026-04-28',
    items: [
      'Log parser: new "Combat DPS" metric — total damage divided by sum of encounter durations only, neutralizing idle / out-of-combat time. Shown on the player overview, the party comparison strip, and as a sortable column in the party table alongside the original DPS.',
      'New "Game News" page (under Neverwinter Community in the menu) — AI-summarized digest of articles from playneverwinter.com/news, filterable by patch notes, lockbox, Zen Store, events, maintenance, and dev blogs.',
    ],
  },
  {
    date: '2026-04-27',
    items: [
      'Added 12 Celestial gear items (IL 4550) from Shackles of Divinity (Master) and 1 Mythic Armor (Abyss Conqueror\'s Mauler Plate) from The Demonweb Pits (Master).',
      'Added 13 new Equip Power bonus definitions covering the new Celestial / Mythic items.',
      'New "What\'s New" page in the menu listing recent updates by date.',
      'Home page: replaced the Simulator tile with a direct Character Editor tile.',
    ],
  },
  {
    date: '2026-04-24',
    items: [
      'Divinity pool & regen reworked across Cleric and Paladin (separate +% Divinity vs +% Divinity Regen handling).',
      'Rotation simulator improvements and conditional-bonus reconciliation.',
    ],
  },
  {
    date: '2026-04-21',
    items: [
      'Optimizer now considers outgoing damage and boss damage modifiers.',
      'External / party buff support for the character editor and rotation sim.',
      'In-app feedback button in the toolbar.',
    ],
  },
  {
    date: '2026-04-19',
    items: [
      'Optimizer: elixir support, item exclusion list, visual UI cleanup.',
    ],
  },
  {
    date: '2026-04-18',
    items: [
      'Ranger powers/feats added through the dailies tier.',
      'More rotation simulator improvements.',
    ],
  },
  {
    date: '2026-04-11',
    items: [
      'Class data filled in across Trickster Rogue, Paladin, Oathbound Paladin, Barbarian, Ranger, and Warlock.',
      'Shirt and Trousers slots added.',
    ],
  },
  {
    date: '2026-04-10',
    items: [
      'Renamed "Profile Editor" to Character Editor across the site.',
      'Mobile-responsive layout pass.',
      'Cookie consent dialog and Microsoft Clarity analytics opt-in.',
      'Optimizer ships its first usable version.',
    ],
  },
  {
    date: '2026-04-08',
    items: [
      'Rotation Simulator with per-power proc detail.',
      'Profile Compare mode for side-by-side build comparison.',
      'Soulweaver Warlock support.',
      'Tooltip improvements throughout the editor.',
    ],
  },
  {
    date: '2026-04-03',
    items: [
      'Custom gear items can be added directly in the editor.',
      'Build bundle export / share for the rotation simulator.',
      'Companion role search and mag (magnitude) calculator.',
    ],
  },
  {
    date: '2026-03-26',
    items: [
      'Single source of truth refactor for stat aggregation across editor, sim and optimizer.',
      'Quality / rarity colors now displayed on item names everywhere.',
      'Well-rested buff and bonus enchantment slot.',
      'Manual stat adjustment field for fine-tuning.',
    ],
  },
  {
    date: '2026-03-25',
    items: [
      'Paragon selector with full feat (5 OR-pairs) and class-feature support.',
      'Artifact slot integration with set-bonus tracking.',
      'Conditional bonuses can be toggled per build.',
    ],
  },
  {
    date: '2026-03-19',
    items: [
      'Initial Character Profile Editor: races, racial traits, classes, paragons, skills.',
      'Combat log parser: pet damage now split out from player damage.',
    ],
  },
  {
    date: '2026-03-10',
    items: [
      'Healing Calculator and Recharge Speed page added under Combat System.',
      'Companion Stable Simulator.',
      'Light / dark theme toggle in the toolbar.',
      'Insignia bonus and mount data fixes.',
    ],
  },
  {
    date: '2026-03-01',
    items: [
      'Legacy JanneNW content imported under the "Old JanneNW Site" menu — Mod 16 / Mod 19 mechanics writeups, formulas, and the M19 stat calculator.',
    ],
  },
];
